package com.evoapps.evoshift.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import kotlinx.coroutines.delay

/**
 * A text field's own text and cursor, kept on the field instead of round-tripping through the
 * caller's state. On iOS a `String` field whose new text came back a frame later (a view model's
 * state, a StateFlow) put the cursor in front of the last typed character. The field now keeps
 * typing locally. A [value] it sent itself (its echo, possibly late and out of order while typing
 * fast) never replaces it. Any other change does, with the cursor at the end: a reset, a value
 * loaded later, the caller filtering or formatting the text.
 */
class StableText internal constructor(initial: String, private val send: () -> (String) -> Unit) {
    var value by mutableStateOf(TextFieldValue(initial, TextRange(initial.length)))
        private set
    private var lastParam = initial
    // The last few texts sent, so the echoes of fast typing arriving late are recognised.
    private val sent = ArrayDeque<String>()

    internal fun sync(param: String) {
        if (param == lastParam) return
        lastParam = param
        if (param == value.text || param in sent) return
        sent.clear()
        value = TextFieldValue(param, TextRange(param.length))
    }

    /**
     * The caller kept a text other than the field's (it refused an edit, e.g. a PIN field ignoring a
     * letter): show the caller's text. Called a moment after typing, so a caller that is only late
     * has caught up by then.
     */
    internal fun settle(param: String) {
        lastParam = param
        if (param == value.text) return
        sent.clear()
        value = TextFieldValue(param, TextRange(param.length))
    }

    val onValueChange: (TextFieldValue) -> Unit = { new ->
        val changed = new.text != value.text
        value = new
        if (changed) {
            sent.addLast(new.text)
            while (sent.size > 8) sent.removeFirst()
            send()(new.text)
        }
    }
}

/** [StableText] for a field showing [value], reporting edits to [onValueChange]. */
@Composable
fun rememberStableText(value: String, onValueChange: (String) -> Unit): StableText {
    val latest = remember { mutableStateOf(onValueChange) }
    latest.value = onValueChange
    val state = remember { StableText(value) { latest.value } }
    state.sync(value)
    LaunchedEffect(state.value.text, value) {
        if (value != state.value.text) {
            delay(SETTLE_MS)
            state.settle(value)
        }
    }
    return state
}

/** How long a caller may take to echo an edit before its own text wins (see [StableText.settle]). */
internal const val SETTLE_MS = 300L
